import { isCurrency } from "./currency-utility";
import {
  IReferenceData,
  isMoreRecent,
  ReferenceDataSource,
  type ReferenceDataView,
} from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import {
  AllowedCurrencies,
  CurrencyType,
  DefaultLocationPerCurrency,
  DefaultSystemLocation,
  UserLocationType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { isOfType } from "@bitwarden/web-vault/app/models/types/general-types";
import {
  DefaultSymbolStoreModel,
  SymbolModelVersion,
  SymbolStoreModel,
} from "../../store/symbol.store.model";
import { IModelView } from "../model.view";
import {
  SymbolGroup,
  SymbolGroupEnum,
} from "@bitwarden/web-vault/app/models/types/symbol-group.types";

/** Default group for symbols with no stored group (v0 records, or newly generated currency symbols). */
export const defaultGroupForCode = (code: string): SymbolGroup =>
  isCurrency(code) ? SymbolGroupEnum.cash : SymbolGroupEnum.securities;

export const isManual = (source: string): boolean => {
  return source === ReferenceDataSource.MANUAL;
};

const getLocation = (currency: string) => {
  return isOfType<CurrencyType>(currency, AllowedCurrencies)
    ? DefaultLocationPerCurrency[currency]
    : null;
};

export const getLocationFromCurrency = (currency: string) => {
  return getLocation(currency) ?? DefaultSystemLocation;
};

// TODO: @jun need to update the code with the new source field in the ReferenceDataView
export const getSouceFromReferenceData = (referenceData: IReferenceData): ReferenceDataSource => {
  return isCurrency(referenceData.base) ? ReferenceDataSource.FOREX : ReferenceDataSource.MANUAL;
};

export const fromReferenceData = (
  referenceData: ReferenceDataView,
  name: string = referenceData.base,
) => {
  return VaultSymbolView.fromReferenceData(referenceData, name);
};

export const fromStoreModel = (model: SymbolStoreModel): SymbolView => {
  return VaultSymbolView.fromStoreModel(model);
};

export const clone = (src: SymbolView) => {
  return VaultSymbolView.fromView(src);
};

export const copy = (dst: SymbolView, src: SymbolView) => {
  // Implement the logic to create a SymbolView from the view data
  dst.name = src.name;
  dst.code = src.code;
  dst.group = src.group;
  if (src.sources) {
    dst.sources = { ...dst.sources, ...src.sources };
  }
  src.history.forEach((h) => dst.addReference(h));
  dst.currencyRate = src.currencyRate;
};

const isVaultSymbolView = (symbol: SymbolView | VaultSymbolView): symbol is VaultSymbolView => {
  const vaultSymbol = symbol as VaultSymbolView;
  return !!(vaultSymbol.toStoreModel && vaultSymbol.vaultId && vaultSymbol.id);
};

export interface ISymbolView {
  name: string;
  code: string; // the symbol code
  sources: Record<string, string>;
  history: IReferenceData[];
  rate: number;
  c_asOf: IReferenceData;
  group: SymbolGroup;
}

/**
 * SymbolView is a view model for a symbol.
 *
 * Since this is a generaded data,
 * better instantiate it with the fromReferenceData method within symbol.generated.collection.ts.
 * Make it abstract to prevent instantiation.
 */
export abstract class SymbolView implements ISymbolView {
  name: string;
  code: string; // the symbol code
  sources: Record<string, string> = {};
  private c_toLatestBaseRate?: IReferenceData;
  currencyRate = 1;
  group: SymbolGroup;

  constructor(code: string, name: string) {
    this.code = code;
    this.name = name;
    this.group = defaultGroupForCode(code);
  }

  isCurrency(): boolean {
    return this.code.length === 3 && isCurrency(this.code);
  }

  isManual(): boolean {
    return isManual(this.c_toLatestBaseRate.source);
  }

  equals(symbol: string): boolean {
    return this.c_toLatestBaseRate.base === symbol;
  }

  /**
   * Returns the latest base rate for the symbol.
   */
  get c_asOf() {
    return this.c_toLatestBaseRate;
  }

  abstract get history(): IReferenceData[];

  addReference(referenceData: IReferenceData) {
    this.c_toLatestBaseRate = referenceData;
    return this;
  }

  get location(): UserLocationType {
    const cross = this.c_asOf?.symbol;
    const base = this.c_asOf?.base ?? this.code;
    return getLocation(cross) ?? getLocation(base) ?? DefaultSystemLocation;
  }

  abstract link(view: SymbolView): void;

  get rate(): number {
    return this.c_asOf.rate * this.currencyRate;
  }
}

/**
 * Do not expose this class to the outside world.
 * So that only can be instantiated with the fromReferenceData method.
 *
 * This is used to represent a symbol is generated from reference data, which
 * symbol in the reference data is the system preference currency.
 *
 * TODO: Only currency symbol is gererated this way. But the user created symbols keep
 * whatever source they have currently since we do not have a way to qurey the real time
 * reference data for them.
 */
class GeneratedSymbolView extends SymbolView {
  private references = new Map<string, ReferenceDataView>();

  constructor(code: string) {
    super(code, "");
  }

  addReference(reference: ReferenceDataView) {
    if (!this.c_asOf || isMoreRecent(reference, this.c_asOf as ReferenceDataView)) {
      super.addReference(reference);
    }

    // Store the reference in the map for later use
    this.references.set(reference.id, reference);
    return this;
  }

  link(view: SymbolView): void {
    if (view.c_asOf.base !== this.c_asOf.symbol) {
      throw new Error(
        `Cannot link SymbolView ${view.code} to GeneratedSymbolView ${this.code} because their base symbols do not match.`,
      );
    }
    this.currencyRate = view.c_asOf.rate;
  }

  get history(): IReferenceData[] {
    return [...this.references.values()].sort((a, b) => (isMoreRecent(b, a) ? 1 : -1));
  }
}

class VaultSymbolView extends GeneratedSymbolView implements IModelView {
  version: SymbolModelVersion;
  vaultId: string;
  id: string;
  dateCreated: Date;
  dateModified: Date;

  protected constructor(code: string) {
    super(code);
    this.version = DefaultSymbolStoreModel.v;
    this.vaultId = null;
    this.id = crypto.randomUUID();
    this.vaultId = null; // View object outside of fromStoreModel do not have vaultId properties.
    this.dateCreated = new Date();
    this.dateModified = new Date();
  }

  static fromView(view: SymbolView | VaultSymbolView): VaultSymbolView {
    const vaultSymbolView = new VaultSymbolView(view.code);
    copy(vaultSymbolView, view);

    if (isVaultSymbolView(view)) {
      vaultSymbolView.id = view.id;
      vaultSymbolView.vaultId = view.vaultId;
      vaultSymbolView.dateCreated = view.dateCreated;
      vaultSymbolView.dateModified = new Date();
      vaultSymbolView.version = view.version;
    }

    return vaultSymbolView;
  }

  static fromStoreModel(model: SymbolStoreModel): VaultSymbolView {
    const storedCodes = Object.values(model.scs || {});
    const code = storedCodes.length > 0 ? storedCodes[0] : model.nm;
    const view = new VaultSymbolView(code);
    view.name = model.nm;
    view.id = model.id;
    view.vaultId = model.vid;
    view.dateCreated = new Date(model.dc);
    view.dateModified = new Date(model.dm);
    view.version = model.v;
    view.sources = { ...model.scs };
    view.group = model.grp ?? defaultGroupForCode(code);
    return view;
  }

  static fromReferenceData(
    referenceData: ReferenceDataView,
    name: string = referenceData.base,
  ): VaultSymbolView {
    const view = new VaultSymbolView(referenceData.base);
    view.name = name;
    view.addReference(referenceData);
    view.sources[referenceData.source] = referenceData.base;
    return view;
  }

  toStoreModel(): SymbolStoreModel {
    const model: SymbolStoreModel = {
      id: this.id,
      vid: this.vaultId,
      dc: this.dateCreated.toISOString(),
      dm: this.dateModified.toISOString(),
      v: this.version,
      nm: this.name,
      scs: { ...this.sources },
      grp: this.group,
    };
    return model;
  }
}
